import { LOCKUP_ASPECT, LOCKUP_PATHS, LOCKUP_VIEWBOX, MARK_ASPECT, MARK_PATHS, MARK_VIEWBOX } from './paths.js';

export type LogoVariant = 'lockup' | 'mark';
export type LogoTone = 'white' | 'black' | 'brand' | 'current';
/** @deprecated use LogoTone */
export type LogoColor = LogoTone | 'currentColor';

export interface LogoProps {
  /** Wordmark plus mark (default) or the mark alone. */
  variant?: LogoVariant;
  /** Rendered height in px. Width follows the official aspect ratio. Default 18 (rail size). */
  height?: number;
  /** Solid white on dark surfaces (default), solid black on light, brand red for the mark, or currentColor. */
  tone?: LogoTone;
  /** @deprecated alias of `tone` kept for the skeleton API. */
  color?: LogoColor;
  className?: string;
  /** Accessible name. Pass an empty string for a decorative logo. */
  title?: string;
}

const FILLS: Record<LogoTone, string> = {
  white: '#FFFFFF',
  black: '#000000',
  brand: 'var(--brand)',
  current: 'currentColor',
};

/**
 * Agency lockup and mark, rendered from the official SVG geometry (DESIGN_SYSTEM section 6; files in
 * ./agency-*.svg). Never enclose the mark in a shape; never place the lockup on tinted backgrounds.
 */
export function Logo({ variant = 'lockup', height = 18, tone, color, className, title = 'Agency' }: LogoProps) {
  const resolved: LogoTone = tone ?? (color === 'currentColor' ? 'current' : color ?? 'white');
  const isLockup = variant === 'lockup';
  const width = Math.round(height * (isLockup ? LOCKUP_ASPECT : MARK_ASPECT));
  const paths = isLockup ? LOCKUP_PATHS : MARK_PATHS;
  const decorative = title === '';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={isLockup ? LOCKUP_VIEWBOX : MARK_VIEWBOX}
      width={width}
      height={height}
      style={{ width: 'auto', height, display: 'block', flexShrink: 0 }}
      fill={FILLS[resolved]}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : title}
      className={className}
      data-variant={variant}
      data-tone={resolved}
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}
